import { useTranslation } from 'core/i18n/i18n';
import { useCallback, useMemo } from 'react';
import {
    ActiveDirectoryDetailsStep,
    ActiveDirectoryDetailsSteps,
    TestConnectionState,
    TestConnectionStatus,
} from '../types/active-directory.type';

interface UseActiveDirectoryFooterProps {
    currentStep: ActiveDirectoryDetailsStep;
    handleNext: VoidFunction;
    handleSave: (isSaveAndExit?: boolean) => void;
    handleClose: VoidFunction;
    handleBack: VoidFunction;
    loading: boolean;
    getIsStepValid: (step: ActiveDirectoryDetailsStep) => boolean;
    testConnectionState: TestConnectionState;
}

export const useActiveDirectoryFooter = ({
    currentStep,
    handleNext,
    handleSave,
    handleClose,
    handleBack,
    loading,
    getIsStepValid,
    testConnectionState,
}: UseActiveDirectoryFooterProps) => {
    const { t } = useTranslation();

    const testConnectionSuccess = testConnectionState.status === TestConnectionStatus.SUCCESS;

    const handleFooterPrimaryButtonClick = useCallback(() => {
        if (currentStep === ActiveDirectoryDetailsSteps.CONNECTION) {
            handleNext();
            return;
        }
        handleSave();
    }, [currentStep, handleNext, handleSave]);

    const handleFooterSecondaryButtonClick = useCallback(() => {
        if (currentStep !== ActiveDirectoryDetailsSteps.CONNECTION) {
            handleBack();
            return;
        }

        if (testConnectionSuccess) {
            handleSave(true);
        } else {
            handleClose();
        }
    }, [currentStep, testConnectionSuccess, handleSave, handleBack, handleClose]);

    const isFooterSecondaryButtonDisabled = loading;
    const isFooterPrimaryButtonDisabled = useMemo(() => {
        if (loading) {
            return true;
        }

        if (!getIsStepValid(currentStep)) {
            return true;
        }

        return false;
    }, [currentStep, getIsStepValid, loading]);

    const footerSecondaryButtonText = useMemo(() => {
        const isCurrentStepConnection = currentStep === ActiveDirectoryDetailsSteps.CONNECTION;

        if (!isCurrentStepConnection) {
            return t('settings.integrations.active-directory.detail.footer.button.back');
        }

        return testConnectionSuccess
            ? t('settings.integrations.active-directory.detail.footer.button.saveAndExit')
            : t('settings.integrations.active-directory.detail.footer.button.cancel');
    }, [currentStep, testConnectionSuccess, t]);

    const footerPrimaryButtonText =
        currentStep === ActiveDirectoryDetailsSteps.CONNECTION
            ? t('settings.integrations.active-directory.detail.footer.button.next')
            : t('settings.integrations.active-directory.detail.footer.button.save');

    return {
        isFooterPrimaryButtonDisabled,
        isFooterSecondaryButtonDisabled,
        footerPrimaryButtonText,
        footerSecondaryButtonText,
        handleFooterPrimaryButtonClick,
        handleFooterSecondaryButtonClick,
    };
};
